import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { FileDown, Loader2 } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { formatCurrency } from '../../utils/format';
import { dateRangesFilter, getMonthObjectiveLabel, getMonthRangeDates, getPeriodRanges, isDateInRanges } from '../../utils/period';

interface MonthlyPerformance {
  month: string;
  monthOnly: string;
  year: number;
  objective: number;
  sales: number;
  potential_sales: number;
  transaction_count: number;
  average_sale: number;
  cumulative_sales: number;
  cumulative_objective: number;
  annual_objective: number;
}

interface AgencyPerformanceStatsProps {
  fiscalYear: number;
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
}

export default function AgencyPerformanceStats({ fiscalYear, period, selectedPeriod }: AgencyPerformanceStatsProps) {
  const [selectedAgency, setSelectedAgency] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agencies, setAgencies] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyPerformance[]>([]);
  const [comparisonData, setComparisonData] = useState<MonthlyPerformance[]>([]);
  const [comparisonYear, setComparisonYear] = useState<number | null>(null);
  const [showObjectives, setShowObjectives] = useState(true);
  const [showPotential, setShowPotential] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAgencies();
  }, []);

  useEffect(() => {
    if (selectedAgency) {
      loadPerformanceData();
      if (showComparison && comparisonYear) {
        loadComparisonData();
      }
    }
  }, [selectedAgency, fiscalYear, period, selectedPeriod, comparisonYear, showComparison]);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select(`
          name,
          company,
          has_custom_objectives,
          monthly_objectives,
          agency_annual_objectives!left (
            fiscal_year,
            annual_objective
          )
        `)
        .eq('active', true)
        .order('company')
        .order('name');

      if (error) throw error;

      setAgencies(data || []);
      if (data && data.length > 0) {
        setSelectedAgency(data[0].name);
      }
    } catch (err: any) {
      console.error('Error loading agencies:', err);
      setError(err.message);
    }
  };

  const loadPerformanceData = async () => {
    try {
      setLoading(true);

      let startDate: string;
      let endDate: string;

      if (period === 'year') {
        startDate = `${fiscalYear}-04-01`;
        endDate = `${fiscalYear + 1}-03-31`;
      } else if (period === 'quarter') {
        const quarterMap = {
          '1': { start: '04', end: '06' },
          '2': { start: '07', end: '09' },
          '3': { start: '10', end: '12' },
          '4': { start: '01', end: '03' }
        };
        const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
        const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
        startDate = `${year}-${quarter.start}-01`;
        endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
      } else {
        ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, fiscalYear));
      }

      const ranges = getPeriodRanges(period, selectedPeriod, fiscalYear, startDate, endDate);

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          fees_without_tax,
          status,
          compromise_date,
          transaction_retrocessions (
            amount
          ),
          transaction_agencies!inner (
            agency
          ),
          transaction_negotiators (
            negotiator_id,
            role,
            percentage
          )
        `)
        .eq('transaction_agencies.agency', selectedAgency)
        .or(dateRangesFilter('compromise_date', ranges));

      if (transactionsError) throw transactionsError;

      const agency = agencies.find(a => a.name === selectedAgency);
      if (!agency) throw new Error('Agency not found');

      let annualObjective = 0;
      if (agency.has_custom_objectives && agency.monthly_objectives?.[fiscalYear]) {
        const monthlyObjectives = agency.monthly_objectives[fiscalYear];
        annualObjective = Object.values(monthlyObjectives).reduce((sum, val) => sum + (val || 0), 0);
      } else {
        // Sinon utiliser l'objectif annuel standard
        const objective = agency.agency_annual_objectives?.find(
          obj => obj.fiscal_year === fiscalYear
        );
        annualObjective = objective?.annual_objective || 0;
      }

      const monthlyObjective = Math.round(annualObjective / 12);

      const monthlyStats = new Map<string, MonthlyPerformance>();
      let currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      let cumulativeObjective = 0;
      let cumulativeSales = 0;

      while (currentDate <= endDateObj) {
        const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
        if (!isDateInRanges(`${monthKey}-01`, ranges)) {
          currentDate.setMonth(currentDate.getMonth() + 1);
          continue;
        }
        const monthName = new Date(currentDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        const monthOnly = new Date(currentDate).toLocaleDateString('fr-FR', { month: 'long' });
        
        let objective = monthlyObjective;
        cumulativeObjective += objective;

        monthlyStats.set(monthKey, {
          month: monthName,
          monthOnly,
          year: currentDate.getFullYear(),
          objective,
          sales: 0,
          potential_sales: 0,
          transaction_count: 0,
          average_sale: 0,
          cumulative_sales: 0,
          cumulative_objective: cumulativeObjective,
          annual_objective: annualObjective
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      transactionsData?.forEach(transaction => {
        const date = new Date(transaction.compromise_date);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        const stats = monthlyStats.get(monthKey);
        if (!stats) return;

        // Calculer les honoraires nets HT
        const totalRetrocessions = transaction.transaction_retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
        const netFees = transaction.fees_without_tax - totalRetrocessions;

        if (transaction.status === 'potentiel') {
          stats.potential_sales += netFees;
        } else {
          stats.sales += netFees;
          stats.transaction_count++;
        }
      });

      let runningTotal = 0;
      monthlyStats.forEach(stats => {
        runningTotal += stats.sales;
        stats.cumulative_sales = runningTotal;
        stats.average_sale = stats.transaction_count ? stats.sales / stats.transaction_count : 0;
      });

      const sortedData = Array.from(monthlyStats.values())
        .sort((a, b) => {
          const dateA = new Date(a.month);
          const dateB = new Date(b.month);
          return dateA.getTime() - dateB.getTime();
        });

      setMonthlyData(sortedData);
    } catch (err: any) {
      console.error('Error loading performance data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadComparisonData = async () => {
    if (!comparisonYear) return;

    try {
      let startDate: string;
      let endDate: string;

      if (period === 'year') {
        startDate = `${comparisonYear}-04-01`;
        endDate = `${comparisonYear + 1}-03-31`;
      } else if (period === 'quarter') {
        const quarterMap = {
          '1': { start: '04', end: '06' },
          '2': { start: '07', end: '09' },
          '3': { start: '10', end: '12' },
          '4': { start: '01', end: '03' }
        };
        const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
        const year = selectedPeriod === '4' ? comparisonYear + 1 : comparisonYear;
        startDate = `${year}-${quarter.start}-01`;
        endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
      } else {
        ({ startDate, endDate } = getMonthRangeDates(selectedPeriod, comparisonYear));
      }

      const ranges = getPeriodRanges(period, selectedPeriod, comparisonYear, startDate, endDate);

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          fees_without_tax,
          status,
          compromise_date,
          transaction_retrocessions (
            amount
          ),
          transaction_agencies!inner (
            agency
          ),
          transaction_negotiators (
            negotiator_id,
            role,
            percentage
          )
        `)
        .eq('transaction_agencies.agency', selectedAgency)
        .or(dateRangesFilter('compromise_date', ranges));

      if (transactionsError) throw transactionsError;

      const agency = agencies.find(a => a.name === selectedAgency);
      if (!agency) throw new Error('Agency not found');

      const monthlyStats = new Map<string, MonthlyPerformance>();
      let currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      let cumulativeObjective = 0;
      let cumulativeSales = 0;

      while (currentDate <= endDateObj) {
        const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
        if (!isDateInRanges(`${monthKey}-01`, ranges)) {
          currentDate.setMonth(currentDate.getMonth() + 1);
          continue;
        }
        const monthName = new Date(currentDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        const monthOnly = new Date(currentDate).toLocaleDateString('fr-FR', { month: 'long' });
        
        let objective = 0;
        if (agency.has_custom_objectives && agency.monthly_objectives) {
          const yearObj = agency.monthly_objectives[currentDate.getFullYear().toString()];
          if (yearObj) {
            const monthNum = (currentDate.getMonth() + 1).toString().padStart(2, '0');
            objective = yearObj[monthNum] || 0;
          }
        } else {
          const annualObjective = agency.agency_annual_objectives?.find(
            obj => obj.fiscal_year === comparisonYear
          )?.annual_objective || 0;
          objective = Math.round(annualObjective / 12);
        }

        cumulativeObjective += objective;

        monthlyStats.set(monthKey, {
          month: monthName,
          monthOnly,
          year: currentDate.getFullYear(),
          objective,
          sales: 0,
          potential_sales: 0,
          transaction_count: 0,
          average_sale: 0,
          cumulative_sales: 0,
          cumulative_objective: cumulativeObjective,
          annual_objective: objective * 12
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      transactionsData?.forEach(transaction => {
        const date = new Date(transaction.compromise_date);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        const stats = monthlyStats.get(monthKey);
        if (!stats) return;

        const totalRetrocessions = transaction.transaction_retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
        const netFees = transaction.fees_without_tax - totalRetrocessions;

        if (transaction.status === 'potentiel') {
          stats.potential_sales += netFees;
        } else {
          stats.sales += netFees;
          stats.transaction_count++;
        }
      });

      let runningTotal = 0;
      monthlyStats.forEach(stats => {
        runningTotal += stats.sales;
        stats.cumulative_sales = runningTotal;
        stats.average_sale = stats.transaction_count ? stats.sales / stats.transaction_count : 0;
      });

      const sortedData = Array.from(monthlyStats.values())
        .sort((a, b) => {
          const dateA = new Date(a.month);
          const dateB = new Date(b.month);
          return dateA.getTime() - dateB.getTime();
        });

      setComparisonData(sortedData);
    } catch (err: any) {
      console.error('Error loading comparison data:', err);
      setError(err.message);
    }
  };

  const handleExportExcel = () => {
    const data = monthlyData.map(month => ({
      'Mois': month.month,
      'Objectif': month.objective,
      'CA Réalisé': month.sales,
      'CA Potentiel': month.potential_sales,
      'Nombre de ventes': month.transaction_count,
      'Moyenne par vente': month.average_sale
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Performance Agence');

    const fileName = `performance_agence_${selectedAgency}_${fiscalYear}-${fiscalYear + 1}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const totalStats = useMemo(() => {
    return monthlyData.reduce((acc, month) => ({
      objective: acc.objective + month.objective,
      sales: acc.sales + month.sales,
      potential_sales: acc.potential_sales + month.potential_sales,
      transaction_count: acc.transaction_count + month.transaction_count
    }), { objective: 0, sales: 0, potential_sales: 0, transaction_count: 0 });
  }, [monthlyData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <select
            value={selectedAgency}
            onChange={(e) => setSelectedAgency(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            {agencies.map((agency) => (
              <option key={agency.name} value={agency.name}>
                {agency.name} ({agency.company})
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <FileDown size={18} />
            <span>Excel</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-sm">
          <h3 className="text-lg font-medium mb-4">Objectif {period === 'year' ? 'Annuel' : period === 'quarter' ? 'Trimestriel' : getMonthObjectiveLabel(selectedPeriod)}</h3>
          <p className="text-3xl font-bold">{formatCurrency(totalStats.objective)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-sm">
          <h3 className="text-lg font-medium mb-4">CA Total</h3>
          <p className="text-3xl font-bold">{formatCurrency(totalStats.sales)}</p>
          <p className="mt-2 text-sm">
            {((totalStats.sales / totalStats.objective) * 100).toFixed(1)}% de l'objectif
          </p>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-sm">
          <h3 className="text-lg font-medium mb-4">CA Potentiel</h3>
          <p className="text-3xl font-bold">{formatCurrency(totalStats.potential_sales)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-sm">
          <h3 className="text-lg font-medium mb-4">Nombre de ventes</h3>
          <p className="text-3xl font-bold">{totalStats.transaction_count}</p>
        </div>
      </div>

      <div className="flex items-center space-x-6 bg-white p-4 rounded-lg shadow-sm">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showObjectives}
            onChange={(e) => setShowObjectives(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Afficher les objectifs
          </span>
        </label>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showPotential}
            onChange={(e) => setShowPotential(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Afficher le CA potentiel
          </span>
        </label>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showComparison}
            onChange={(e) => {
              setShowComparison(e.target.checked);
              if (!e.target.checked) {
                setComparisonYear(null);
              }
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Activer la comparaison
          </span>
        </label>

        {showComparison && (
          <select
            value={comparisonYear || ''}
            onChange={(e) => setComparisonYear(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          >
            <option value="">Sélectionner une année</option>
            {Array.from({ length: 3 }, (_, i) => fiscalYear - (i + 1)).map(year => (
              <option key={year} value={year}>
                {year}-{year + 1}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution du CA</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="colorObjective" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#93C5FD" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#93C5FD" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPotential" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#93C5FD" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#93C5FD" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey={showComparison ? "monthOnly" : "month"}
                angle={-45} 
                textAnchor="end" 
                height={100}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                width={100}
              />
              <Tooltip 
                formatter={(value: any) => formatCurrency(value)}
                labelFormatter={(label) => showComparison ? `${label}` : label}
              />
              <Legend />
              
              {showObjectives && (
                <Area
                  type="monotone"
                  dataKey="annual_objective"
                  name="Objectif Annuel"
                  stroke="none"
                  fill="url(#colorObjective)"
                  stackId="background"
                />
              )}

              <Area
                type="monotone"
                dataKey="cumulative_sales"
                name={`CA ${fiscalYear}-${fiscalYear + 1}`}
                stroke="#3B82F6"
                fillOpacity={1}
                fill="url(#colorSales)"
                stackId="1"
              />

              {showPotential && (
                <Area
                  type="monotone"
                  dataKey="potential_sales"
                  name="CA Potentiel"
                  stroke="#93C5FD"
                  fillOpacity={1}
                  fill="url(#colorPotential)"
                  stackId="1"
                />
              )}

              {showComparison && comparisonYear && (
                <Area
                  type="monotone"
                  dataKey="cumulative_sales"
                  data={comparisonData}
                  name={`CA ${comparisonYear}-${comparisonYear + 1}`}
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  fill="none"
                  strokeDasharray="5 5"
                />
              )}

              {showObjectives && monthlyData.length > 0 && (
                <ReferenceLine
                  y={monthlyData[0].annual_objective}
                  stroke="#93C5FD"
                  strokeDasharray="3 3"
                  label={{
                    value: `Objectif Annuel: ${formatCurrency(monthlyData[0].annual_objective)}`,
                    position: 'right',
                    fill: '#93C5FD'
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mois
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Objectif
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CA Réalisé
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CA Cumulé
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CA Potentiel
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Réalisation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nb Ventes
              </th>
              {showComparison && comparisonYear && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CA {comparisonYear}-{comparisonYear + 1}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Évolution
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {monthlyData.map((month) => {
              const comparisonMonth = comparisonData.find(m => m.monthOnly === month.monthOnly);
              const evolution = comparisonMonth ? 
                ((month.sales - comparisonMonth.sales) / comparisonMonth.sales) * 100 : 
                0;

              return (
                <tr key={month.month} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {showComparison ? month.monthOnly : month.month}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(month.objective)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                    {formatCurrency(month.sales)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {formatCurrency(month.cumulative_sales)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-amber-600">
                    {formatCurrency(month.potential_sales)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      (month.cumulative_sales / month.cumulative_objective) >= 1 ? 'text-green-600' :
                      (month.cumulative_sales / month.cumulative_objective) >= 0.75 ? 'text-blue-600' :
                      (month.cumulative_sales / month.cumulative_objective) >= 0.5 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {((month.cumulative_sales / month.cumulative_objective) * 100).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {month.transaction_count}
                  </td>
                  {showComparison && comparisonYear && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {comparisonMonth ? formatCurrency(comparisonMonth.sales) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {comparisonMonth && (
                          <div className={`text-sm font-medium ${evolution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {evolution >= 0 ? '+' : ''}{evolution.toFixed(1)}%
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}